<template>
  <div class="clients-range-slider">
    <div class="number-of-clients-line">
      <span class="text-cinza-d10">{{ localeString(clients.total_count || 0, 0) }}</span>
      <span class="text-cinza-l10">{{ title(localeString(clients.total_count || 0, 0)) }}</span>
    </div>

    <div class="clients-chart">
      <v-progress-linear v-if="sliderLoading || filteredLegendContent.length === 0" :indeterminate="sliderLoading" height="12" color="grey" rounded />
      <div
        v-for="(item, index) in filteredLegendContent"
        :key="item.color"
        :class="{ 'first-chart': index === 0, 'last-chart': index === filteredLegendContent.length - 1 }"
        :style="`width: ${item.percentage}%; height: 12px; background: ${item.color}`"
      />
    </div>

    <div class="legend-content">
      <div v-for="item in legendContent" :key="item.color" class="legend-item">
        <div class="item-square" :style="`background-color: ${item.color}`" />
        <span class="item-percentage" :style="{ color: item.color }">{{ localeString(item.percentage, 1) }}%</span>
        <span>|</span>
        <div>
          <span class="text-cinza-d10">{{ localeString(item.value || 0, 0) }}</span>
          <span class="text-cinza-l10"> {{ item.label }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { localeString } from '@/config/helpers.js'
import { QUERY_GET_AGGREGATED_CLIENTS_STATUS_INFO } from '@/modules/maria/campanha-da-maria-v2/graphql'

export default {
  name: 'ClientsRangeSlider',
  props: {
    channel: {
      type: String,
      required: true,
    },
    segments: {
      type: Array,
      default: () => [],
    },
  },
  data: () => ({
    sliderLoading: false,
    clients: {},
    clientsDistribution: {
      client_active_count: 0,
      client_inactive_count: 0,
      client_lost_count: 0,
      client_never_bought_count: 0,
      client_non_loyalty_count: 0,
      client_at_risk_count: 0,
      client_losses_count: 0,
      client_recent_inactive_count: 0,
    },
  }),
  computed: {
    isWhatsappChannel() {
      return this.channel === 'whatsapp'
    },
    nonLoyalty() {
      const nonLoyaltyCount = localeString(this.clients.client_non_loyalty_count, 0)
      return nonLoyaltyCount === 1 ? 'contato fora do clube' : `contatos fora do clube`
    },
    legendContent() {
      return [
        {
          color: '#0FC94D',
          percentage: this.clientsDistribution.client_active_count || 0,
          value: this.clients.client_active_count,
          label: this.getLabel(this.clients.client_active_count, 'Ativo', 'Ativos'),
        },
        {
          color: '#F3AF00',
          percentage: this.clientsDistribution.client_recent_inactive_count || 0,
          value: this.clients.client_recent_inactive_count,
          label: this.getLabel(this.clients.client_recent_inactive_count, 'Inativo recente', 'Inativos recentes'),
        },
        {
          color: '#F36600',
          percentage: this.clientsDistribution.client_at_risk_count || 0,
          value: this.clients.client_at_risk_count,
          label: this.getLabel(this.clients.client_at_risk_count, 'Risco de perder', 'Risco de perder'),
        },
        {
          color: '#B3280D',
          percentage: this.clientsDistribution.client_losses_count || 0,
          value: this.clients.client_losses_count,
          label: this.getLabel(this.clients.client_losses_count, 'Perdido', 'Perdidos'),
        },
        {
          color: '#969EA7',
          percentage: this.clientsDistribution.client_never_bought_count || 0,
          value: this.clients.client_never_bought_count,
          label: this.getLabel(this.clients.client_never_bought_count, 'Nunca comprou', 'Nunca compraram'),
        },
      ]
    },
    filteredLegendContent() {
      return this.legendContent.filter((item) => item.value > 0)
    },
  },
  async mounted() {
    await this.getData()
  },
  methods: {
    title(clientsLength) {
      const labelClients = clientsLength > 1 ? 'clientes' : 'cliente'
      const labelPhones = clientsLength > 1 ? 'números de telefones válidos' : 'número de telefone válido'
      const messages = {
        whatsapp: `${labelClients} cadastrados no WhatsApp`,
        push: `${labelClients} possuem o aplicativo instalado`,
        sms: `${labelPhones} de ${labelClients}`,
        email: `${labelClients} com um endereço de e-mail válido`,
      }

      return messages[this.channel] || ''
    },
    getLabel(value = 0, singularLabel, pluralLabel) {
      const count = localeString(value, 0)
      return count === 1 ? `${singularLabel}` : `${pluralLabel}`
    },
    async getData() {
      const segments_ids = this.segments.map((segment) => segment._id)
      try {
        this.sliderLoading = true
        await this.$apollo
          .query({
            query: QUERY_GET_AGGREGATED_CLIENTS_STATUS_INFO,
            fetchPolicy: 'no-cache',
            variables: {
              segments_ids,
            },
          })
          .then((response) => {
            this.clients = response.data.getAggregatedClientStatusInfo[this.channel]
            this.clientsDistribution = {
              client_active_count: parseFloat(((this.clients?.client_active_count / this.clients?.total_count) * 100).toPrecision(3)),
              client_at_risk_count: parseFloat(((this.clients?.client_at_risk_count / this.clients?.total_count) * 100).toPrecision(3)),
              client_losses_count: parseFloat(((this.clients?.client_losses_count / this.clients?.total_count) * 100).toPrecision(3)),
              client_recent_inactive_count: parseFloat(((this.clients?.client_recent_inactive_count / this.clients?.total_count) * 100).toPrecision(3)),
              client_never_bought_count: parseFloat(((this.clients?.client_never_bought_count / this.clients?.total_count) * 100).toPrecision(3)),
              client_non_loyalty_count: parseFloat(((this.clients?.client_non_loyalty_count / this.clients?.total_count) * 100).toPrecision(3)),
            }
          }, [])
      } catch (error) {
        this.$snackbar({ message: 'Erro ao buscar dados no servidor', snackbarColor: 'error' })
      } finally {
        this.sliderLoading = false
      }
    },
  },
}
</script>

<style lang="scss">
.clients-range-slider {
  display: flex;
  flex-direction: column;
  gap: 12px;

  .number-of-clients-line {
    font-size: 14px;
    font-weight: 700;
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 4px;
  }

  .text-cinza-d10 {
    color: $cinza-d10;
  }

  .text-cinza-l10 {
    color: $cinza-l10;
  }

  .clients-chart {
    height: 12px;
    width: 100%;
    display: inline-flex;

    .first-chart {
      border-top-left-radius: 6px;
      border-bottom-left-radius: 6px;
    }

    .last-chart {
      border-top-right-radius: 6px;
      border-bottom-right-radius: 6px;
    }
  }

  .legend-content {
    display: flex;
    align-items: center;
    gap: 48px;
    flex-wrap: wrap;
    font-size: 14px;

    .legend-item {
      display: flex;
      align-items: center;
      gap: 6px;

      .item-square {
        width: 12px;
        height: 12px;
        border-radius: 4px;
      }

      .item-percentage {
        font-size: 14px;
        font-weight: 700;
      }
    }
  }
}
</style>
